import type { ActivityEvent, Funnel, Lead, Membership, Stage, Tag, Task } from "../../lib/types";
import type { FinanceReceipt, RevenueGoal } from "../finance/contracts";
import { leadReceivedCents } from "../finance/contracts";
import { buildRows, type PeriodId } from "../leads/list";
import {
  conversionText,
  dailySeries,
  dayLabels,
  funnelFlow,
  goalOrbit,
  homeKpis,
  homeRange,
  type DayRange,
} from "../home/metrics";
import { channelPerformance } from "../analysis/metrics";
import { civilDate } from "../notifications/feed";

/**
 * O retrato que o relatório de prospecção imprime.
 *
 * Os números saem das mesmas fórmulas da Início e da Análise (§10): o PDF não
 * tem conta própria, então a página e o arquivo não discordam. A carteira e as
 * tarefas são fotografia da base atual — o período só recorta os gráficos,
 * como o protótipo explica na janela.
 */

export type ReportModel = "simple" | "full";

export const REPORT_PERIODS = [
  { id: "hoje", label: "Hoje", hint: "hoje" },
  { id: "semana", label: "Últimos 7 dias", hint: "últimos 7 dias" },
  { id: "mes", label: "Últimos 30 dias", hint: "últimos 30 dias" },
  { id: "tri", label: "Este trimestre", hint: "este trimestre" },
] as const satisfies readonly { id: PeriodId; label: string; hint: string }[];

export type ReportPeriod = (typeof REPORT_PERIODS)[number]["id"];

export function isReportPeriod(value: string | null | undefined): value is ReportPeriod {
  return REPORT_PERIODS.some((item) => item.id === value);
}

export const REPORT_MODELS: readonly { id: ReportModel; title: string; detail: string }[] = [
  { id: "simple", title: "Simples", detail: "Resumo da Home" },
  { id: "full", title: "Completo", detail: "Desempenho e operação" },
];

/** O que cada modelo inclui, na ordem das páginas. */
export function reportSections(model: ReportModel): string[] {
  const base = ["Receita e indicadores do período", "Conversão do funil e meta trimestral", "Clientes em destaque"];
  return model === "full"
    ? [...base, "Canais e evolução por intervalo", "Carteira, tarefas e atenção", "Detalhamento dos leads"]
    : base;
}

export function reportScopeNote(model: ReportModel): string {
  return model === "full"
    ? "O período afeta os gráficos. Carteira e operação mostram a base atual, como no PDF existente."
    : "Resumo da Home no período escolhido, com meta trimestral e clientes em destaque.";
}

export interface ReportSnapshot {
  model: ReportModel;
  hint: string;
  brand: string;
  agency: string;
  generatedAt: string;
  demo: boolean;
  kpis: { revenueCents: number; leads: number; won: number; conversion: string };
  series: { labels: string[]; revenue: number[]; leadRevenue: number[]; leadCounts: number[]; won: number[] };
  funnelName: string;
  stages: { name: string; reached: number; averageDays: number | null }[];
  goal: { achievedCents: number; amountCents: number; elapsedRatio: number } | null;
  top: { name: string; company: string; ltvCents: number }[];
  channels: { channel: string; revenueCents: number }[];
  portfolio: {
    total: number;
    active: number;
    won: number;
    lost: number;
    activeValue: number;
    wonValue: number;
    lostValue: number;
    ltvTotalCents: number;
    ltvClientAverageCents: number;
    heatAverage: number;
    quietWeek: number;
  };
  operation: {
    pending: number;
    late: number;
    done: number;
    openNotices: number;
    next: { title: string; when: string }[];
  };
  leads: { name: string; company: string; stage: string; situation: string; value: number; ltvCents: number; heat: number }[];
}

export interface ReportInput {
  model: ReportModel;
  period: ReportPeriod;
  now: string;
  funnelId?: string;
  agency: string;
  demo: boolean;
  leads: readonly Lead[];
  memberships: readonly Membership[];
  stages: readonly Stage[];
  funnels: readonly Funnel[];
  tags: readonly Tag[];
  tasks: readonly Task[];
  events: readonly ActivityEvent[];
  receipts: readonly FinanceReceipt[];
  revenueGoals: readonly RevenueGoal[];
  openNotices: number;
}

const stamp = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const shortDay = new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "short" });
const clock = new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit" });

export function buildReportSnapshot(input: ReportInput): ReportSnapshot {
  const { now } = input;
  const range: DayRange = homeRange(input.period, now);
  const leads = input.leads.filter((lead) => !lead.archived);
  const kpis = homeKpis({ leads, receipts: input.receipts, range });
  const firstSales = input.receipts.filter((receipt) => receipt.firstSale);

  const funnels = input.funnels.filter((funnel) => !funnel.archived);
  const funnel = funnels.find((item) => item.id === input.funnelId) ?? funnels[0];
  const flow = funnel
    ? funnelFlow({
        funnelId: funnel.id,
        stages: input.stages,
        memberships: input.memberships,
        leads,
        events: input.events,
        range,
      })
    : [];

  const orbit = goalOrbit(input.revenueGoals, input.receipts, now);
  const ltv = (lead: Lead) => leadReceivedCents(input.receipts, lead.id);

  const rows = buildRows({
    leads,
    memberships: input.memberships,
    stages: input.stages,
    funnels: input.funnels,
    tags: input.tags,
    tasks: input.tasks,
    now,
  });
  const clients = rows.filter((row) => row.isClient);
  const active = rows.filter((row) => row.status === "active" && !row.isClient);
  const lost = rows.filter((row) => row.status === "lost");
  const value = (list: typeof rows) => list.reduce((total, row) => total + (row.lead.potentialValue || 0), 0);
  const clientLtv = clients.map((row) => ltv(row.lead));

  const pending = input.tasks.filter((task) => task.status !== "done");
  const today = civilDate(now, "America/Sao_Paulo");
  const late = pending.filter((task) => civilDate(task.dueAt, "America/Sao_Paulo") < today);

  const channels = channelPerformance({
    leads,
    memberships: input.memberships,
    receipts: input.receipts,
    range,
    previous: range,
  })
    .filter((row) => row.revenueCents > 0 || row.leads > 0)
    .sort((a, b) => b.revenueCents - a.revenueCents)
    .slice(0, 6)
    .map((row) => ({ channel: row.channel, revenueCents: row.revenueCents }));

  const top = [...clients]
    .map((row) => ({ name: row.lead.name, company: row.lead.company, ltvCents: ltv(row.lead) }))
    .sort((a, b) => b.ltvCents - a.ltvCents)
    .slice(0, 3);

  const situation = (row: (typeof rows)[number]) =>
    row.isClient ? "Cliente" : row.status === "lost" ? "Perdido" : "Ativo";

  return {
    model: input.model,
    hint: REPORT_PERIODS.find((item) => item.id === input.period)?.hint ?? "",
    brand: "COSMOSLEADS",
    agency: input.agency,
    generatedAt: stamp.format(new Date(now)).replace(",", ""),
    demo: input.demo,
    kpis: {
      revenueCents: kpis.revenueCents,
      leads: kpis.leads,
      won: kpis.won,
      conversion: conversionText(kpis.conversion),
    },
    series: {
      labels: dayLabels(range),
      revenue: kpis.series.revenue,
      leadRevenue: dailySeries(
        range,
        firstSales.map((receipt) => receipt.receivedOn),
        firstSales.map((receipt) => receipt.amountCents),
      ),
      leadCounts: kpis.series.leads,
      won: kpis.series.won,
    },
    funnelName: funnel?.name ?? "Sem funil",
    stages: flow.map((item) => ({ name: item.stage.name, reached: item.reached, averageDays: item.averageDays })),
    goal: orbit.goal
      ? { achievedCents: orbit.achievedCents, amountCents: orbit.goal.amountCents, elapsedRatio: orbit.elapsedRatio }
      : null,
    top,
    channels,
    portfolio: {
      total: rows.length,
      active: active.length,
      won: clients.length,
      lost: lost.length,
      activeValue: value(active),
      wonValue: value(clients),
      lostValue: value(lost),
      ltvTotalCents: rows.reduce((total, row) => total + ltv(row.lead), 0),
      ltvClientAverageCents: clientLtv.length ? Math.round(clientLtv.reduce((a, b) => a + b, 0) / clientLtv.length) : 0,
      heatAverage: rows.length ? rows.reduce((total, row) => total + row.heat, 0) / rows.length : 0,
      quietWeek: rows.filter((row) => row.quietDays >= 7).length,
    },
    operation: {
      pending: pending.length,
      late: late.length,
      done: input.tasks.length - pending.length,
      openNotices: input.openNotices,
      next: [...pending]
        .sort((a, b) => a.dueAt.localeCompare(b.dueAt))
        .slice(0, 3)
        .map((task) => ({
          title: task.description,
          when: `${shortDay.format(new Date(task.dueAt)).replace(".", "")} · ${clock.format(new Date(task.dueAt))}`,
        })),
    },
    leads: rows
      .map((row) => ({
        name: row.lead.name,
        company: row.lead.company,
        stage: row.stageName,
        situation: situation(row),
        value: row.lead.potentialValue || 0,
        ltvCents: ltv(row.lead),
        heat: row.heat,
      }))
      .sort((a, b) => b.ltvCents - a.ltvCents),
  };
}
